/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor, dlsZIndex} from '../../theme/theme';

export type CurrentPanelStyleProps = {
  panelTop?: number;
  rightOffset?: number;
};

/**
 * Provides the current theme's floating map panel card styles shared by the
 * Map Layers and Map Views panels: the card (`panel`), its fixed position
 * over the map (`floating` - `panelTop` / `rightOffset` from the viewport's
 * top / right, 64px / 24px by default) and its 56px header row (`header`).
 *
 * ```typescript
 *
 *   import {useCurrentPanelStyles} from
 *     '@ion/dls/src/maps/layers/currentPanelStyles';
 *   ...
 *
 *   const panelClasses = useCurrentPanelStyles({panelTop: 80, rightOffset: 24});
 *
 *   <div className={classnames(panelClasses.panel, panelClasses.floating)}>
 *     <div className={panelClasses.header}>
 *       ...
 *     </div>
 *   </div>
 *
 * ```
 *
 * @param   props The card's viewport `panelTop` and `rightOffset`.
 * @returns       The `panel`, `floating` and `header` class names.
 */
export const useCurrentPanelStyles = makeStyles(theme => ({
  // the card look - its height is intentionally NOT fixed, it grows and
  // shrinks with its content ...
  panel : {
    display         : 'flex',
    flexDirection   : 'column',
    backgroundColor : dlsColor('white'),
    borderTop       : `2px solid ${dlsColor('pastelTeal')}`,
    boxShadow       : '0px 4px 4px rgba(0, 0, 0, 0.25)',
    borderRadius    : 12,
    boxSizing       : 'border-box'
  },
  // floats the card over the map (every caller except the mobile bottom
  // sheet, where it sits inline) - capped by the same maxHeight legacy's
  // own lists use ...
  floating : {
    position                       : 'fixed',
    top                            : ({panelTop}: CurrentPanelStyleProps) => panelTop ?? 64,
    right                          : ({rightOffset}: CurrentPanelStyleProps) => rightOffset ?? 24,
    width                          : 360,
    maxHeight                      : 'calc(100vh - 300px)',
    zIndex                         : dlsZIndex('trackingFilterSelectedFiltersModal') as any,
    // a fixed 360px card plus its right offset overflows a phone's
    // viewport, so on mobile it spans the screen with 16px gutters ...
    [theme.breakpoints.down('sm')] : {
      width : 'auto',
      left  : 16,
      right : ({rightOffset}: CurrentPanelStyleProps) => rightOffset ?? 16
    }
  },
  header : {
    display        : 'flex',
    flexShrink     : 0,
    alignItems     : 'center',
    justifyContent : 'space-between',
    height         : 56,
    padding        : '0 8px 0 16px'
  }
}));

// the current theme's shared Chip fills every chip blue and gives the
// selected ones a paler fill, so a map layer's chips - its labels, its
// traffic sub-layers - fill blue with a highlighted border only while
// selected, and stay white with a neutral border otherwise ...
const useCurrentLayerChipStyles = makeStyles(() => ({
  chip : {
    '&&' : {
      backgroundColor : dlsColor('white'),
      borderColor     : dlsColor('grey5')
    }
  },
  selectedChip : {
    '&&' : {
      backgroundColor    : dlsColor('paleBlue'),
      borderColor        : dlsColor('aaBlue'),
      '&:focus, &:hover' : {
        backgroundColor : dlsColor('paleBlue')
      }
    }
  }
}));

/**
 * Provides a hook which resolves the current theme's map layer chip classes
 * (e.g. a layer's label chips, the traffic layer's sub-layer chips) - a
 * chip fills blue only while selected. Legacy keeps the shared Chip's own
 * styling.
 *
 * ```typescript
 *
 *   import {useCurrentLayerChipClasses} from
 *     '@ion/dls/src/maps/layers/currentPanelStyles';
 *   ...
 *
 *   const getChipClasses = useCurrentLayerChipClasses(isCurrent);
 *
 *   <Chip classes={getChipClasses(isSelected)} isSelected={isSelected} />
 *
 * ```
 *
 * @param   isCurrent `true` in the current theme.
 * @returns           The chip's `classes` for its selected state, or
 *                    `undefined` in the legacy theme.
 */
export const useCurrentLayerChipClasses = (isCurrent: boolean) => {
  const classes = useCurrentLayerChipStyles();
  return (isSelected: boolean) => (
    isCurrent ? {root: isSelected ? classes.selectedChip : classes.chip} : undefined
  );
};

export default useCurrentPanelStyles;
